import { createContext, useContext } from 'react';

import type { ProviderPreference } from '../clientProviderPreference';
import type { LastUsedMethod } from '../lastUsedWebAuthMethod';

/**
 * Which screen the shell is on. A known `'entry'` member for autocomplete,
 * plus an open string so module tickets can add step names without changing
 * the shell's type — the shell has no opinion about which steps exist.
 */
export type AccountFormStep = 'entry' | (string & {});

export const ENTRY_STEP: AccountFormStep = 'entry';

/** Shared because both the entry and password screens navigate to it. */
export const RESET_PASSWORD_STEP: AccountFormStep = 'reset-password';

export interface AccountFormSuccessResult {
  email: string;
  /**
   * Post-submit destination the write-path returned (a `register`/`logIn`
   * `redirect_to`), when the module had one to surface.
   */
  redirectTo?: string;
}

export interface AccountFormContextValue {
  /** Fires when a module completes the form's flow (e.g. login/register). */
  onSuccess?: (result: AccountFormSuccessResult) => void;
  /** Fires when a module's flow errors out. */
  onError?: (error: unknown) => void;
  /** Post-login destination modules forward to social auth buttons. */
  redirectTo?: string;
  /** Email carried across steps so later screens can render "Signing in as {email}". */
  email: string;
  setEmail: (email: string) => void;
  step: AccountFormStep;
  /** Advancing a step clears `formError`, so a screen's failure can't outlive it. */
  setStep: (step: AccountFormStep) => void;
  /**
   * Form-level failure message(s) for the screen's single alert region. Shared
   * rather than module-local so a composition can render the region where the
   * design puts it — the Figma's entry screen carries it above the social stack,
   * not beside the field whose submit failed.
   *
   * Whatever composes those modules owes the pairing: render one
   * `AuthFormError` bound to this value, or the failures published here are
   * invisible. Components with no shared region to reach — a redirect-auth
   * button mounted outside a form — keep rendering their own error inline.
   */
  formError: string | string[] | null;
  setFormError: (error: string | string[] | null) => void;
  /**
   * The affordance a returning seller last signed in with, or undefined when
   * there is none to surface: no cookie, or a stored method with no button.
   * Modules compare against this to decide which of them owns the "Last used"
   * treatment, so no composition has to thread a badge renderer down to the
   * module that draws it.
   */
  lastUsedMethod?: LastUsedMethod;
  /**
   * The provider the rendering client suggests the seller already has an account
   * with, or undefined when the client is one we decline to classify or the
   * surface never opted in. Lower precedence than `lastUsedMethod`: a method the
   * seller actually used outranks a guess from their browser.
   *
   * Resolved here rather than per module so every module orders against the same
   * decision, and so the classification runs once per form rather than once per
   * button.
   */
  clientPreferredProvider?: ProviderPreference;
  /**
   * Reports that a "Last used" badge was shown, at most once per name for the
   * life of this form.
   *
   * The form owns this rather than the module drawing the badge, for two
   * reasons. A step change remounts modules — leaving the entry screen and
   * returning through switch-account is enough — and an impression the seller
   * already had should not count twice against the follow-through denominator.
   * And the dedupe is only claimed once the send succeeds, so a blocked
   * telemetry sink cannot silence a name for the rest of the session; sequencing
   * that correctly at every call site is not a thing worth trusting to callers.
   */
  reportBadgeImpression: (targetName: string) => void;
  /**
   * Called after Continue with email only when the lookup is an existing
   * password account. Return true to show the login-link offer as the main
   * action, with password secondary. New and generated-password accounts
   * never call this.
   */
  onExistingPasswordEmailContinue?: () => Promise<boolean>;
}

export const AccountFormContext = createContext<AccountFormContextValue | null>(null);

/**
 * Reach the shared form state from any module, whether mounted under the
 * batteries-included `<AccountFormV2 />` or hand-built à la carte under a
 * bare `<AccountFormProvider>`. Must be called under one of them — throws
 * otherwise, since a silent default (a no-op `setEmail`/`setStep`) would hide
 * a module wired up wrong.
 */
export function useAccountForm(): AccountFormContextValue {
  const context = useContext(AccountFormContext);
  if (context == null) {
    throw new Error('useAccountForm must be used within an AccountFormProvider.');
  }
  return context;
}
